import {
  buildJalaliGrid, buildGregorianGrid,
  jalaliToGregorian, JALALI_MONTHS, GREGORIAN_MONTHS,
  JALALI_DAYS_SHORT, GREGORIAN_DAYS_SHORT,
  todayJalali, isJalaliLeap,
} from '../utils/jalali.js';
import dayjs from 'dayjs';

function _todayIso() {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,'0')}-${String(n.getDate()).padStart(2,'0')}`;
}

function _isoToJalaliParts(iso) {
  if (!iso) return null;
  const d = dayjs(iso);
  if (!d.isValid()) return null;
  // Use dayjs with locale fa to extract Jalali parts safely
  const dfa = d.locale('fa');
  return {
    y: parseInt(dfa.format('YYYY')),
    m: parseInt(dfa.format('M')),
    d: parseInt(dfa.format('D')),
    h: d.hour(),
    min: d.minute(),
  };
}

function _isoToGregorianParts(iso) {
  if (!iso) return null;
  const d = dayjs(iso);
  if (!d.isValid()) return null;
  return { y: d.year(), m: d.month() + 1, d: d.date(), h: d.hour(), min: d.minute() };
}

function _jalaliPartsToIso(jy, jm, jd, h = 0, min = 0) {
  const { gy, gm, gd } = jalaliToGregorian(jy, jm, jd);
  return `${gy}-${String(gm).padStart(2,'0')}-${String(gd).padStart(2,'0')}T${String(h).padStart(2,'0')}:${String(min).padStart(2,'0')}`;
}

function _gregPartsToIso(gy, gm, gd, h = 0, min = 0) {
  return `${gy}-${String(gm).padStart(2,'0')}-${String(gd).padStart(2,'0')}T${String(h).padStart(2,'0')}:${String(min).padStart(2,'0')}`;
}

function _daysInJalaliMonth(jy, jm) {
  if (jm <= 6) return 31;
  if (jm <= 11) return 30;
  return isJalaliLeap(jy) ? 30 : 29;
}

/**
 * datePicker({ value, onChange, withTime, jalali })
 *
 * value    — initial ISO date or datetime string
 * onChange  — function(isoString) called on selection
 * withTime — show hour/minute pickers (default false)
 * jalali   — boolean, read from Alpine store if omitted
 */
export function datePicker({ value = '', onChange = null, withTime = false, jalali = null } = {}) {
  return {
    // ── Config ──────────────────────────────────────────
    withTime,
    get isJalali() {
      if (jalali !== null) return jalali;
      return (window.Alpine?.store('i18n')?.resolvedCalendar?.() ?? 'gregorian') === 'jalali';
    },

    // ── State ────────────────────────────────────────────
    open: false,
    viewYear: 0,
    viewMonth: 0,
    selYear: 0,
    selMonth: 0,
    selDay: 0,
    selHour: 0,
    selMin: 0,
    hasValue: false,

    // ── Init ─────────────────────────────────────────────
    init() {
      this._parseValue(value);
      this.$watch('isJalali', () => this._recalcView());
    },

    _parseValue(iso) {
      if (!iso) {
        const today = _todayIso();
        this._initViewFromIso(today);
        this.hasValue = false;
        return;
      }
      this.hasValue = true;
      this._initViewFromIso(iso);
    },

    _initViewFromIso(iso) {
      if (this.isJalali) {
        const p = _isoToJalaliParts(iso);
        if (!p) return;
        this.viewYear  = p.y;
        this.viewMonth = p.m;
        this.selYear   = p.y;
        this.selMonth  = p.m;
        this.selDay    = p.d;
        this.selHour   = p.h;
        this.selMin    = p.min;
      } else {
        const p = _isoToGregorianParts(iso);
        if (!p) return;
        this.viewYear  = p.y;
        this.viewMonth = p.m;
        this.selYear   = p.y;
        this.selMonth  = p.m;
        this.selDay    = p.d;
        this.selHour   = p.h;
        this.selMin    = p.min;
      }
    },

    _recalcView() {
      // Re-init view when calendar mode switches; keep same date
      const iso = this.currentIso;
      if (iso) this._initViewFromIso(iso);
    },

    // ── Computed ─────────────────────────────────────────
    get currentIso() {
      if (!this.selDay) return '';
      try {
        return this.isJalali
          ? _jalaliPartsToIso(this.selYear, this.selMonth, this.selDay, this.selHour, this.selMin)
          : _gregPartsToIso(this.selYear, this.selMonth, this.selDay, this.selHour, this.selMin);
      } catch { return ''; }
    },

    get displayText() {
      if (!this.hasValue || !this.selDay) return '';
      const d = this.selDay ? String(this.selDay).padStart(2,'0') : '';
      const m = this.selMonth ? String(this.selMonth).padStart(2,'0') : '';
      const t = this.withTime ? ` ${String(this.selHour).padStart(2,'0')}:${String(this.selMin).padStart(2,'0')}` : '';
      if (this.isJalali) {
        return `${this.selYear}/${m}/${d}${t}`;
      } else {
        return `${this.selYear}-${m}-${d}${t}`;
      }
    },

    get monthLabel() {
      return this.isJalali
        ? (JALALI_MONTHS[this.viewMonth - 1] ?? '') + ' ' + this.viewYear
        : (GREGORIAN_MONTHS[this.viewMonth - 1] ?? '') + ' ' + this.viewYear;
    },

    get dayHeaders() {
      return this.isJalali ? JALALI_DAYS_SHORT : GREGORIAN_DAYS_SHORT;
    },

    get weeks() {
      try {
        return this.isJalali
          ? buildJalaliGrid(this.viewYear, this.viewMonth)
          : buildGregorianGrid(this.viewYear, this.viewMonth);
      } catch { return []; }
    },

    isSelected(cell) {
      if (!cell || !this.selDay) return false;
      return cell.iso === this._selIso();
    },

    _selIso() {
      if (!this.selDay) return '';
      try {
        if (this.isJalali) {
          const { gy, gm, gd } = jalaliToGregorian(this.selYear, this.selMonth, this.selDay);
          return `${gy}-${String(gm).padStart(2,'0')}-${String(gd).padStart(2,'0')}`;
        } else {
          return `${this.selYear}-${String(this.selMonth).padStart(2,'0')}-${String(this.selDay).padStart(2,'0')}`;
        }
      } catch { return ''; }
    },

    // ── Navigation ───────────────────────────────────────
    prevMonth() {
      if (this.viewMonth === 1) { this.viewMonth = 12; this.viewYear--; }
      else this.viewMonth--;
    },

    nextMonth() {
      if (this.viewMonth === 12) { this.viewMonth = 1; this.viewYear++; }
      else this.viewMonth++;
    },

    // ── Selection ────────────────────────────────────────
    selectCell(cell) {
      if (!cell) return;
      this.hasValue = true;
      if (this.isJalali) {
        // cell.iso is Gregorian ISO — convert back to Jalali
        const p = _isoToJalaliParts(cell.iso);
        if (!p) return;
        this.selYear = p.y; this.selMonth = p.m; this.selDay = p.d;
      } else {
        const p = _isoToGregorianParts(cell.iso);
        if (!p) return;
        this.selYear = p.y; this.selMonth = p.m; this.selDay = p.d;
      }
      this._emit();
      if (!this.withTime) this.open = false;
    },

    setHour(h) { this.selHour = Math.max(0, Math.min(23, parseInt(h) || 0)); this._emit(); },
    setMin(m)  { this.selMin  = Math.max(0, Math.min(59, parseInt(m) || 0)); this._emit(); },

    _emit() {
      const iso = this.currentIso;
      if (onChange) onChange(iso);
      this.$dispatch('date-pick', { value: iso });
    },

    clear() {
      this.hasValue = false;
      this.selDay = 0;
      if (onChange) onChange('');
      this.$dispatch('date-pick', { value: '' });
      this.open = false;
    },

  };
}

export const datePickerHtml = `
<div class="relative" @click.outside="open = false">
  <div class="flex items-center border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-blue-500 bg-white">
    <button type="button" @click="open = !open"
      class="flex-1 text-left px-3 py-2 text-sm focus:outline-none"
      :class="hasValue ? 'text-gray-900' : 'text-gray-400'">
      <span x-text="hasValue ? displayText : (isJalali ? 'انتخاب تاریخ…' : 'Pick a date…')"></span>
    </button>
    <button type="button" x-show="hasValue" @click.stop="clear()"
      class="px-2 text-gray-400 hover:text-red-500 text-lg leading-none">×</button>
    <button type="button" @click="open = !open"
      class="px-3 py-2 text-gray-400 hover:text-blue-500 border-l border-gray-200">
      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
      </svg>
    </button>
  </div>

  <div x-show="open" x-transition
    class="absolute z-50 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl p-3 w-72"
    :class="isJalali ? 'right-0' : 'left-0'" style="direction: ltr;">

    <div class="flex items-center justify-between mb-3" :style="isJalali ? 'direction:rtl' : ''">
      <button type="button" @click="prevMonth()"
        class="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
        </svg>
      </button>
      <span class="text-sm font-semibold text-gray-700" x-text="monthLabel"></span>
      <button type="button" @click="nextMonth()"
        class="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
        </svg>
      </button>
    </div>

    <div class="grid grid-cols-7 mb-1" :style="isJalali ? 'direction:rtl' : ''">
      <template x-for="h in dayHeaders" :key="h">
        <div class="text-center text-xs font-semibold text-gray-400 py-1" x-text="h"></div>
      </template>
    </div>

    <div :style="isJalali ? 'direction:rtl' : ''">
      <template x-for="(week, wi) in weeks" :key="wi">
        <div class="grid grid-cols-7">
          <template x-for="(cell, di) in week" :key="di">
            <div>
              <button x-show="cell !== null" type="button"
                @click="cell && selectCell(cell)"
                class="w-full aspect-square flex items-center justify-center text-xs rounded-lg transition-colors"
                :class="cell && isSelected(cell)
                  ? 'bg-blue-600 text-white font-bold'
                  : cell && cell.isToday
                    ? 'border border-blue-400 text-blue-600 font-semibold hover:bg-blue-50'
                    : 'text-gray-700 hover:bg-gray-100'"
                x-text="cell ? cell.mainDay : ''">
              </button>
              <div x-show="cell === null" class="w-full aspect-square"></div>
            </div>
          </template>
        </div>
      </template>
    </div>

    <template x-if="withTime">
      <div class="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100 justify-center">
        <svg class="w-4 h-4 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
        <input type="number" min="0" max="23"
          :value="String(selHour).padStart(2,'0')"
          @change="setHour($event.target.value)"
          class="w-14 border border-gray-300 rounded-lg text-center text-sm px-1 py-1 focus:ring-2 focus:ring-blue-500 outline-none">
        <span class="text-gray-500 font-bold">:</span>
        <input type="number" min="0" max="59"
          :value="String(selMin).padStart(2,'0')"
          @change="setMin($event.target.value)"
          class="w-14 border border-gray-300 rounded-lg text-center text-sm px-1 py-1 focus:ring-2 focus:ring-blue-500 outline-none">
      </div>
    </template>

  </div>
</div>
`;
